/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility Load canonical reusable assistants through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports app environment; TanStack Query; Zod; assistant model and demo data
 * @forbidden routes; direct component API calls; Zustand server entities
 * @public-api via modules/assistants/index.ts
 * @side-effects authenticated GET requests outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); no useGet/useFetch/useLoad prefixes
 * @naming-path plural lowercase nouns; organization is the active-context scope hint
 * @naming-body camelCase; never caller role or server-side identity fields
 * @tests modules/assistants/api/assistants.queries.test.ts
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { ASSISTANTS } from '../../../shared/lib/demo-data';
import type { AssistantRecord } from '../../../shared/lib/model';

export const assistantSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  tone: z.enum(['warm', 'calm', 'playful', 'coach']),
  persona: z.string(),
  hint_limit: z.number().int().min(0).max(4),
  forbidden: z.string(),
  is_default: z.boolean(),
  revision: z.number().int().positive(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const assistantKeys = {
  all: (activeContextId: string) => ['assistants', activeContextId] as const,
};
const assistantsPath = '/api/v1/assistants';

function mapAssistant(value: z.infer<typeof assistantSchema>): AssistantRecord {
  return {
    id: value.id,
    name: value.name,
    emoji: '🤖',
    accent: '#6b38d4',
    tagline: '',
    persona: value.persona,
    tone: value.tone,
    warmth: 60,
    humour: 40,
    strictness: 50,
    brevity: 60,
    address: '同學',
    catchphrase: '',
    greeting: '',
    emojiUse: 'light',
    hintsBeforeTeacher: value.hint_limit,
    answerPolicy: 'never',
    forbidden: value.forbidden,
    replyRules: [],
    isDefault: value.is_default,
    revision: value.revision,
  };
}

export function useAssistants(
  activeContextId: string,
  organizationId?: string | null,
  enabled = true,
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: assistantKeys.all(activeContextId),
    enabled:
      enabled && Boolean(activeContextId) && (environment.demoMode || Boolean(organizationId)),
    queryFn: async (): Promise<AssistantRecord[]> => {
      if (environment.demoMode) return ASSISTANTS.map((item) => ({ ...item }));
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      const payload = await environment.apiClient.request<unknown>(
        `${assistantsPath}?organizationId=${encodeURIComponent(organizationId)}`,
      );
      const result = z.object({ items: z.array(assistantSchema) }).parse(payload);
      return result.items.map(mapAssistant);
    },
    retry: 1,
  });
}

export function parseAssistant(payload: unknown): AssistantRecord {
  return mapAssistant(assistantSchema.parse(payload));
}

export { mapAssistant };
